import { Injectable } from '@angular/core';
import { LocalService } from './local.service';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { LoginParam, RegisterParam, User } from '../../shared/model/auth.model';
import { BehaviorSubject, catchError, filter, first, firstValueFrom, map, take, throwError } from 'rxjs';
import { ConfigService } from './config.service';
import { ApiModel } from '../../shared/model/api.model';
import { ManagementsService } from './managements.service';
import { SokratesSsoService } from './sokrates-sso.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private refreshingToken = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  
  private service = 'recruitment-http/personalia';
  private module = 'auth';

  constructor(
    private localService: LocalService,
    private apiService: ApiService,
    private router: Router,
    private configService: ConfigService,
    private managementsService: ManagementsService,
    private sokratesSsoService: SokratesSsoService,
  ) { }

  isSuperadmin(): boolean {
    const user = this.configService.getUser();
    return user && user.roles.some(role => role.role_name === 'SUPERADMIN');
  }

  isCurrentlySwitched(): boolean {
    return this.localService.getData('account_switch_data') !== null;
  }

  getCurrentSwitchedUser(): any {
    try {
      const switchData = this.localService.getData('account_switch_data');
      return switchData.switchedToUser || null;
    } catch {
      return null;
    }
  }

  getOriginalSuperadmin(): any {
    try {
      const switchData = this.localService.getData('account_switch_data');
      return switchData.originalSuperadmin || null;
    } catch {
      return null;
    }
  }

  hasPermissions(permissions: string[] | string, operator: 'and' | 'or' = 'and'): boolean {
    if (typeof permissions === 'string') {
      permissions = [permissions];
    }

    const userPermissions = this.configService.getPermissions();
    switch (operator) {
      case 'or':
        return permissions.some(permission => userPermissions[permission]);
      case 'and':
        return permissions.every(permission => userPermissions[permission]);
      default:
        return false;
    }
  }

  async logout(): Promise<boolean> {
    return firstValueFrom(this.apiService.post(this.service, `${this.module}/v1/logout`, {}, {
      withCredentials: true,
    })).then(() => {
      // After an SSO login, also end the SSO session; it returns to /auth.
      const ssoLogoutUrl = this.sokratesSsoService.endSessionUrl(this.sokratesSsoService.getIdToken());
      this.sokratesSsoService.setIdToken(null);
      this.localService.clearData();
      if (ssoLogoutUrl) {
        window.location.assign(ssoLogoutUrl);
        return true;
      }
      this.router.navigate(['/pages']);
      window.location.reload();
      return true;
    }).catch(() => {
      return false;
    });
  }

  refreshToken() {
    if (this.refreshingToken) {
      // Kalau lagi refresh, tunggu sampai selesai
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1)
      );
    } else {
      this.refreshingToken = true;
      this.refreshTokenSubject.next(null);

      return this.getRefreshTokenHttpRequest().pipe(
        map(res => {
          const newToken = res.data.token;
          this.configService.setToken(newToken);
          this.configService.setUser(res.data.user);

          this.refreshingToken = false;
          this.refreshTokenSubject.next(newToken);
          return newToken;
        }),
        catchError(err => {
          this.refreshingToken = false;
          this.refreshTokenSubject.next(null);
          return throwError(() => err);
        }),
      );
    }
  }

  getRefreshTokenHttpRequest() {
    return this.apiService.post(this.service, `${this.module}/v1/refresh-token`, {}, {
      withCredentials: true,
    });
  }

  captcha() {
    return this.apiService.get('auth-http', 'v2/captcha');
  }

  login(data: LoginParam) {
    return this.apiService.post(this.service, `${this.module}/v1/login`, data, {
      withCredentials: true,
    });
  }

  /** Finishes "Login with Sokrates" (OIDC): the Personalia backend exchanges the code with the SSO. */
  oidcCallback(data: { code: string; code_verifier: string; nonce: string; redirect_uri: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/oidc-callback`, data, {
      withCredentials: true,
    });
  }

  loginByToken(token: string) {
    return this.apiService.post(this.service, `${this.module}/v1/token/login`, { token });
  }

  async checkAndLoginFromToken(): Promise<boolean> {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      if (!token) return false;

      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState({}, document.title, url.toString());

      // Tunggu sampai observable-nya kelar
      const res: ApiModel<{ user: User, token: string }> = await firstValueFrom(this.loginByToken(token));

      this.configService.setToken(res.data.token);
      this.configService.setUser(res.data.user);

      return true;

    } catch (error) {
      console.error('Token login failed:', error);
      
      await this.router.navigate(['/auth']);
      return false;
    }
  }

  register(data: RegisterParam) {
    return this.apiService.post(this.service, `${this.module}/v1/register`, data, {
      withCredentials: true,
    });
  }

  requestOtp(userUuid: string, lang = this.localService.getData('lang') || 'en') {
    return this.apiService.get(this.service, `${this.module}/v1/otp/request/${userUuid}`, { lang });
  }

  verifyEmail(data: { otp: string, user_uuid: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/user/email/verify`, data);
  }

  resetPasswordRequest(data: { email: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/reset-password/request`, data);
  }

  resetPasswordTokenCheck(token: string) {
    return this.apiService.get(this.service, `${this.module}/v1/reset-password/check/${token}`);
  }

  resetPassword(data: { password: string, confirm_password: string, reset_token: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/reset-password`, data);
  }

  googleLogin(data: { credential: string }) {
    return this.apiService.post(this.service, `${this.module}/v1/google/login`, data, {
      withCredentials: true,
    });
  }

  async switchBackToSuperadmin() {
    if (this.isCurrentlySwitched()) {
      const res = await firstValueFrom(this.refreshToken());

      // Clear switch data from local storage
      this.localService.removeData('account_switch_data');
      window.location.reload();
    };
  }

  async switchAccount(user: User) {
    // Save back superadmin token to localstorage, if saved in account_switch_data
    try {
      const switchData = this.localService.getData('account_switch_data');
      if (switchData.originalToken) {
        this.configService.setToken(switchData.originalToken);
      }
      if (switchData.originalSuperadmin) {
        this.configService.setUser(switchData.originalSuperadmin);
      }
    } catch {
      // Ignore errors
    }

    const res = await firstValueFrom(this.managementsService.switchAccount(user.user_id));

    const switchedUser = res.data.user;
    const newToken = res.data.token;

    // Store the switch in local storage for future reference
    const switchData = {
      originalToken: this.configService.getToken(),
      originalSuperadmin: this.getOriginalSuperadmin() || this.configService.getUser(),
      switchedToUser: switchedUser,
      switchedAt: new Date().toISOString(),
    };

    this.localService.saveData('account_switch_data', switchData);

    // Update current user data in config service
    this.configService.setUser(switchedUser);

    // Update token in config service
    this.configService.setToken(newToken);

    window.location.reload();
  }
}
